import { ImportValidationError } from "@/domain";

export const JSON_IMPORT_MAX_BYTES = 50 * 1024 * 1024;

export type ReadJsonImportOptions = {
  maximumByteLength?: number;
};

function stringByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function enforceImportSize(byteLength: number, maximum: number): void {
  if (!Number.isSafeInteger(maximum) || maximum <= 0) {
    throw new RangeError(
      "The configured JSON import limit must be a positive safe integer.",
    );
  }

  if (byteLength > maximum) {
    throw new ImportValidationError(
      `The selected annotation file is larger than the ${Math.floor(
        maximum / (1024 * 1024),
      )} MiB import limit.`,
    );
  }
}

export async function readJsonImportValue(
  input: Blob | string | unknown,
  options: ReadJsonImportOptions = {},
): Promise<unknown> {
  const maximum = options.maximumByteLength ?? JSON_IMPORT_MAX_BYTES;

  if (input instanceof Blob) {
    enforceImportSize(input.size, maximum);

    let text: string;
    try {
      text = await input.text();
    } catch (error) {
      throw new ImportValidationError(
        "The browser could not read the selected annotation file.",
        { cause: error },
      );
    }

    return readJsonImportValue(text, options);
  }

  if (typeof input === "string") {
    enforceImportSize(stringByteLength(input), maximum);

    try {
      return JSON.parse(input) as unknown;
    } catch (error) {
      throw new ImportValidationError(
        "The selected annotation file is not valid JSON.",
        { cause: error },
      );
    }
  }

  return input;
}
